import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Message, ToolCallState } from './types';
import AiChatMessage from './AiChatMessage';
import AiChatSuggestions from './AiChatSuggestions';
import AiChatQuickActions from './AiChatQuickActions';
import { ChatContainer } from './ChatContainer';
import AiAvatar from './AiAvatar';

const getToolMessage = (t: TFunction, toolName: string): string => {
  const messages: Record<string, string> = {
    getMyProfile: t('aiChat.tools.getMyProfile'),
    getMyAbsenceBalance: t('aiChat.tools.getMyAbsenceBalance'),
    searchDocuments: t('aiChat.tools.searchDocuments'),
    requestAbsence: t('aiChat.tools.requestAbsence'),
    getMyAbsences: t('aiChat.tools.getMyAbsences'),
    getTeamAbsences: t('aiChat.tools.getTeamAbsences'),
    getMyTimeBalance: t('aiChat.tools.getMyTimeBalance'),
    getMyTimesheets: t('aiChat.tools.getMyTimesheets'),
    submitTimesheet: t('aiChat.tools.submitTimesheet'),
    getMyObjectives: t('aiChat.tools.getMyObjectives'),
    getMyPerformanceReview: t('aiChat.tools.getMyPerformanceReview'),
    searchKnowledgeBase: t('aiChat.tools.searchKnowledgeBase'),
  };
  return messages[toolName] ?? toolName;
};

const getGenericMessages = (t: TFunction): string[] => [
  t('aiChat.generic.thinking'),
  t('aiChat.generic.searchingData'),
  t('aiChat.generic.analyzing'),
  t('aiChat.generic.readingDocs'),
];

function useLoadingMessage(
  isLoading: boolean,
  activeToolName: string | null,
): string {
  const { t } = useTranslation();
  const [genericIndex, setGenericIndex] = useState(0);
  const genericMessages = getGenericMessages(t);

  useEffect(() => {
    if (!isLoading || activeToolName) {
      setGenericIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setGenericIndex((prev) => (prev + 1) % genericMessages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isLoading, activeToolName, genericMessages.length]);

  if (activeToolName) {
    return getToolMessage(t, activeToolName);
  }
  return genericMessages[genericIndex];
}

type Props = {
  messages: Message[];
  isLoading: boolean;
  activeToolName: string | null;
  pendingConfirm: ToolCallState | null;
  onConfirm: (toolId: string) => void;
  onCancel: (toolId: string) => void;
  onSuggestionClick: (text: string) => void;
  onNavigate: (route: string) => void;
  onFeedback?: (
    messageId: string,
    rating: 'up' | 'down',
    comment?: string,
  ) => void;
};

const AiChatMessages: React.FC<Props> = ({
  messages,
  isLoading,
  activeToolName,
  pendingConfirm,
  onConfirm,
  onCancel,
  onSuggestionClick,
  onNavigate,
  onFeedback,
}) => {
  const loadingMessage = useLoadingMessage(isLoading, activeToolName);
  if (messages.length === 0) {
    return <AiChatSuggestions onSuggestionClick={onSuggestionClick} />;
  }

  const lastMessage = messages[messages.length - 1];
  const showQuickActions =
    lastMessage.role === 'assistant' && lastMessage.quickActions.length > 0;

  return (
    <ChatContainer className="flex-grow-1">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          padding: '0.75rem 0',
        }}>
        {messages.map((msg) => (
          <AiChatMessage
            key={msg.id}
            message={msg}
            pendingConfirm={pendingConfirm}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onFeedback={onFeedback}
          />
        ))}

        {isLoading && (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <AiAvatar size="sm" />
            <div className="ai-chat-loader">
              <span>
                {loadingMessage}
                <span
                  className="animate-loading-dots"
                  style={{ animationDelay: '0.2s' }}>
                  .
                </span>
                <span
                  className="animate-loading-dots"
                  style={{ animationDelay: '0.4s' }}>
                  .
                </span>
                <span
                  className="animate-loading-dots"
                  style={{ animationDelay: '0.6s' }}>
                  .
                </span>
              </span>
            </div>
          </div>
        )}

        {showQuickActions && (
          <AiChatQuickActions
            quickActions={lastMessage.quickActions}
            onNavigate={onNavigate}
            onInlinePrompt={onSuggestionClick}
          />
        )}
      </div>
    </ChatContainer>
  );
};

export default AiChatMessages;
